import * as React from 'react';
import './Claims.scss';

import { ClaimService, StoreService, UserService, ChannelService } from 'services/ApiService';

import { Claim, Item, ToastState, User, UserType, Channel, AlertClaim, AlertType, ClaimType } from 'types';
import { ExportMode, EXPORT_ALL_MESSAGE, EXPORT_FILTERED_MESSAGE, EXPORT_VISIBLE_MESSAGE } from '../../helpers/export';
import ReactTable, { ReactTableDefaults, Column } from 'react-table';
import Select from 'react-select';
import Toast from 'components/Toast';

import {
  SearchFilter, LinkedDateCell, DropdownFilter,
  LinkedTooltipCell, TooltipHeader, NoDataMessage
} from 'helpers/Table';
import { ExportToCsv } from 'export-to-csv';
import { TableLoader } from '../../components/Loader';
import DropdownButton from '../../components/Dropdown/DropdownButton';
import { formatDate } from '../../helpers/date';
import DropdownContentDiv from 'components/Dropdown/DropdownContentDiv';
import { getAlertClaims } from '../../services/AlertClaimService';

const linker = (props) => `/app/claims/${props.row.type || 'recall'}-${props.row.id}`;
const dateCellLinker = LinkedDateCell(linker);

// const itemsApprovedColumnTemplate = {
//   Header: <TooltipHeader value="Items Approved" />,
//   accessor: 'approvalRatio',
//   filterable: false,
//   className: '', // Remove the class so that capitalization doesn't apply
// };

/**
 * Normalizes a difference value into a sort value -1 or 0 or 1.
 * @param difference The difference between two things being sorted.
 * @returns -1 if the difference is negative, 0 if the difference is 0 or 1 otherwise.
 */
function normalizedSortDifference(difference: number): -1 | 0 | 1 {
  if (difference !== 0) {
    return difference / Math.abs(difference) as -1 | 1;
  }
  return difference;
}

export default class Claims extends React.Component<{},
  {
    claims: Claim<Item>[],
    hideCompletedClaims: boolean,
    hideReturnInvoice: boolean,
    loading: boolean,
    user: User | null,
    channelFilter: Channel[],
    channelOptions: { value: number, label: string }[],
    pages: number,
    showExportFiltered: boolean,
  }> {

  static statusTextOptions = [
    { value: 'completed', label: 'Completed' },
    { value: 'incomplete', label: 'Incomplete' },
    { value: 'lco submitted', label: 'LCO Submitted' },
    { value: 'submitted for adjustment', label: 'Submitted For Adjustment' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved by admin', label: 'Approved By Admin'}
  ];

  static typeOptions = [
    { value: 'adjustment', label: 'Adjustment' },
    { value: 'refund', label: 'Refund' },
    { value: AlertClaim.TYPE_NAME, label: 'Recall ' },
  ];

  static dispositionOptions = [
    { value: 'return', label: 'Return' },
    { value: 'destroy', label: 'Destroy' },
  ];

  static newOptions = [
    { value: 'NEW', label: 'New' },
    { value: ' ', label: 'Viewed' }
  ];

  static columns: Column[] = [
    {
      Header: <TooltipHeader value="Claim #" />,
      accessor: 'id',
      Filter: SearchFilter
    }, {
      Header: <TooltipHeader value="Date Created" />,
      id: 'submitted',
      accessor: 'createDateString',
      Cell: dateCellLinker,
      Filter: SearchFilter
    }, {
      Header: <TooltipHeader value="Status" />,
      accessor: 'statusText',
      Filter: ({ onChange, filter }) => (
        DropdownFilter({ onChange, filter, options: Claims.statusTextOptions })
      ),
      filterMethod: (filter, row) => (
        !filter.value || row[filter.id].toLowerCase() === filter.value.value.toLowerCase()
      ),
      width: 200
    }, {
      Header: 'Type',
      accessor: 'type',
      Filter: ({ onChange, filter }) => (
        DropdownFilter({ onChange, filter, options: Claims.typeOptions })
      ),
      filterMethod: (filter, row) => (
        !filter.value || row[filter.id].toLowerCase() === filter.value.value.toLowerCase()
      ),
      width: 150
    }, {
      Header: <TooltipHeader value="PO" />,
      accessor: 'purchaseNumberList',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    }, {
      Header: <TooltipHeader value="Store Name" />,
      accessor: 'storeOperatorName',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    },
    {
      Header: <TooltipHeader value="Alert #" />,
      accessor: 'alertId',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && `${row[filter.id]}`.toLowerCase().includes(filter.value.toLowerCase());
      }
    },
    {
      Header: <TooltipHeader value="Disposition" />,
      accessor: 'disposition',
      Filter: ({ onChange, filter }) => (
        DropdownFilter({ onChange, filter, options: Claims.dispositionOptions })
      ),
      filterMethod: (filter, row) => (
        !filter.value || row[filter.id].toLowerCase() === filter.value.value.toLowerCase()
      ),
    },
    {
      Header: <TooltipHeader value="Return Invoice" />,
      accessor: 'returnInvoice',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    },
    {
      Header: <TooltipHeader value="Items" />,
      accessor: 'itemNumberList',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    },
    {
      Header: <TooltipHeader value="Assigned To" />,
      accessor: 'assignedToString',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    },
    {
      Header: <TooltipHeader value="New" />,
      accessor: 'notification',
      Filter: ({ onChange, filter }) => (
        DropdownFilter({ onChange, filter, options: Claims.newOptions })
      ),
      filterMethod: (filter, row) => {
        return !filter.value || row[filter.id].toLowerCase() === filter.value.value.toLowerCase();
      },
    }
  ];

  static reporterColumns: Column[] = [
    {
      Header: <TooltipHeader value="Claim #" />,
      accessor: 'id',
      Filter: SearchFilter
    }, {
      Header: <TooltipHeader value="Date Submitted" />,
      id: 'submitted',
      accessor: 'createDateString',
      Filter: SearchFilter
    },
    {
      Header: <TooltipHeader value="Status Date Modified" />,
      id: 'updated',
      accessor: 'updateDateString',
      Filter: SearchFilter,
      sortMethod: (a, b) => {
        a = a === '-' ? +Infinity : a;
        b = b === '-' ? +Infinity : b;
        return normalizedSortDifference(a - b);
      }
    }, {
      Header: <TooltipHeader value="Receival Date" />,
      accessor: 'goodsReceivedDateString',
      Filter: SearchFilter,
      sortMethod: (a, b) => {
        a = a === '-' ? +Infinity : a;
        b = b === '-' ? +Infinity : b;
        return normalizedSortDifference(a - b);
      }
    },
    {
      Header: <TooltipHeader value="PO" />,
      accessor: 'purchaseNumberList',
      Filter: SearchFilter,
    }, {
      Header: <TooltipHeader value="Type" />,
      accessor: 'type',
      Filter: ({ onChange, filter }) => (
        DropdownFilter({ onChange, filter, options: Claims.typeOptions })
      ),
      filterMethod: (filter, row) => (
        !filter.value || row[filter.id].toLowerCase() === filter.value.value.toLowerCase()
      ),
      width: 150
    }, {
      Header: <TooltipHeader value="Status" />,
      accessor: 'statusText',
      Filter: ({ onChange, filter }) => (
        DropdownFilter({ onChange, filter, options: Claims.statusTextOptions })
      ),
      filterMethod: (filter, row) => (
        !filter.value || row[filter.id].toLowerCase() === filter.value.value.toLowerCase()
      ),
      width: 200
    }, {
      Header: <TooltipHeader value="Store Name" />,
      accessor: 'storeOperatorName',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    },
    {
      Header: <TooltipHeader value="Store Number" />,
      accessor: 'storeNumber',
      Filter: SearchFilter,
    }, {
      Header: <TooltipHeader value="Channel" />,
      accessor: 'channelName',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    },
    {
      Header: <TooltipHeader value="Return Invoice" />,
      accessor: 'returnInvoice',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    },
    {
      Header: <TooltipHeader value="Items" />,
      accessor: 'itemNumberList',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    },
    {
      Header: <TooltipHeader value="Item Description" />,
      accessor: 'itemDescriptionList',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    }, {
      Header: <TooltipHeader value="Cases" />,
      accessor: 'cases',
      Filter: SearchFilter
    }, {
      Header: <TooltipHeader value="Units" />,
      accessor: 'units',
      Filter: SearchFilter
    },
    // itemsApprovedColumnTemplate,
    {
      Header: <TooltipHeader value="Decision" />,
      accessor: 'decision',
      Filter: SearchFilter,
      filterMethod: (filter, row) => {
        return row[filter.id] && row[filter.id].toLowerCase().includes(filter.value.toLowerCase());
      }
    }
  ];

  static reporterHeader: string[] = ['Claim Number', 'Date Submitted', 'Status Date Modified', 'Receival Date',
    'Purchase Order Number', 'Type', 'Status', 'Store Name', 'Store Number', 'Channel', 'Return Invoice', 'Item', 'Item Description',
    'Cases', 'Units', 'Decision'];

  static reporterHeaderAccessor: string[] = ['id', 'createDate', 'updateDate', 'goodsReceivedDateString',
    'purchaseNumberList', 'type', 'status', 'storeName', 'storeId', 'channel', 'returnInvoice', 'itemNumber', 'itemDescriptionList',
    'cases', 'units', 'decision'];

  static channelMap = {};
  static allChannelMap = {};

  private tableState;

  static getFilteredData(tableState: any): Claim<Item>[] | undefined {
    if (tableState) {
      return (tableState.sortedData as Claim<Item>[]);
    }
    return undefined;
  }

  constructor(props: {}) {
    super(props);
    this.state = {
      claims: [],
      hideCompletedClaims: true,
      hideReturnInvoice: true,
      loading: false,
      user: null,
      channelFilter: [],
      channelOptions: [],
      pages: -1,
      showExportFiltered: false,
    };

    this.handleTableRender = this.handleTableRender.bind(this);
    this.handleTableFiltersChanged = this.handleTableFiltersChanged.bind(this);
  }

  async transformClaims(claims: Claim<Item>[]) {
    let updatedClaims: Claim<Item>[] = [];
    let channelId: number[] = [];
    if ((this.state.user!!.type === UserType.LCBOReporter)) {
      for (const channel of this.state.user!!.channels) {
        Claims.channelMap[channel.id] = channel.channelName;
        channelId.push(channel.id);
      }
    }

    for (let claim of claims) {
      let ifInChannelFilter: boolean = false;
      let tempChannelId = claim.channelId;

      if ((this.state.user!!.type === UserType.LCBOReporter)) {
        claim.channelName = tempChannelId ? Claims.channelMap[tempChannelId] : '';
      }

      if (this.state.channelFilter.length === 0) {
        if (this.state.user!!.type === UserType.LCBOReporter) {
          ifInChannelFilter = channelId.includes(tempChannelId);
        } else {
          ifInChannelFilter = true;
        }
      } else {
        for (const filter of this.state.channelFilter) {
          if (!ifInChannelFilter) ifInChannelFilter = (tempChannelId === filter.id);
        }
      }

      if (ifInChannelFilter) {
        for (const item of claim.items) {
          let newClaim = this.copyClaim(claim);
          if (newClaim.type === 'refund') {
            newClaim.goodsReceivedDateString = item.receivalDate ?
              formatDate(item.receivalDate) : '-';
          } else if (newClaim.type === 'adjustment') {
            newClaim.goodsReceivedDateString = item.receivalDate ? item.receivalDate.toLocaleString().substring(0, 10) : '-';
          } else {
            newClaim.goodsReceivedDateString = claim.goodsReceivedDate ?
              formatDate(claim.goodsReceivedDate) : '-';
          }

          newClaim.item = item.itemNumber;
          if (typeof item.purchaseNumber === 'number') {
            newClaim.purchaseNumber = item.purchaseNumber;
          }
          newClaim.itemDescription = item.productDescription ? item.productDescription : item.itemName ? item.itemName : '';
          newClaim.decision = item.isApproved ? 'Approved' : '-';
          newClaim.cases = item.cases;
          newClaim.category = item.claimCategory ? item.claimCategory : '-';
          if (typeof item.units === 'number') {
            newClaim.units = item.units;
          } else if (typeof item.unitsClaimed === 'number') {
            newClaim.units = item.unitsClaimed;
          }
          updatedClaims.push(newClaim);
        }
      }
    }
    return updatedClaims;
  }

  async fetchData(tableState: any = null) {
    try {
      let claims: Claim<Item>[] = [];
      const currentUser = await UserService.fetch();

      this.setState({
        loading: true,
        user: currentUser,
        hideReturnInvoice: [UserType.Admin, UserType.LCBO, UserType.LCBOReporter].indexOf(currentUser.type) !== -1,
        channelOptions: currentUser.channels.map(channel => ({
          value: channel.id,
          label: channel.channelName
        }))
      });

      var queryParams = {};
      if (this.state.hideCompletedClaims) {
        let paramKey = 'hideCompleted';
        queryParams[paramKey] = true;
      }

      var queryString = Object.keys(queryParams).map(key => key + '=' + queryParams[key]).join('&');

      if (this.state.user && this.state.user.type === UserType.LCBOReporter) {
        claims = await ClaimService.fetchAllItems(queryString);
      } else {
        const user = this.state.user;
        claims = await ClaimService.fetchAll(queryString, user);
      }

      if (claims.length > 0) {
        const arrayOfStores = await StoreService.fetchAll();
        const arrayOfUsers = UserType.canFetchAllUsers(this.state.user!!.type)
          ? await UserService.getAllUsers()
          : await UserService.getAllGrocerUsers();
        let objOfStores = {};
        let objOfUsers = {};

        for (let store of arrayOfStores) {
          objOfStores[store.id] = {
            storeOperatorName: store.storeOperatorName,
            storeOperatorNumber: store.storeOperatorNumber,
            phoneNumber: store.phoneNumber,
            address: store.address,
            channelId: store.channelId
          };
        }

        for (let user of arrayOfUsers) {
          objOfUsers[user.id] = {
            contactName: user.contactName,
            email: user.email,
          };
        }
        
        claims.forEach(async claim => {
          claim.storeOperatorName = objOfStores[claim.storeId].storeOperatorName;
          claim.storeNumber = objOfStores[claim.storeId].storeOperatorNumber;
          claim.phoneNumber = objOfStores[claim.storeId].phoneNumber;
          claim.address = objOfStores[claim.storeId].address;
          claim.channelId = objOfStores[claim.storeId].channelId;
          claim.createDateString = formatDate(claim.createDate);
          claim.updateDateString = claim.updateDate.toString() !== '-' ? formatDate(claim.updateDate) : '-';
          const claimUser = objOfUsers[claim.userId];
          /* It's possible for some users not to have access to admin user information for example, in which case
          the user who may be listed on the claim won't be visible to the authenticated user. */
          claim.contactName = claimUser ? claimUser.contactName : '';
          claim.email = claimUser ? claimUser.email : '';
          claim.itemPO = claim.itemPO;
          /* Just like above, not all users can see information about other users, so we
          need to conditionally render other users' information. */
          const assignedToUserInfo = objOfUsers[claim.assignedTo];
          claim.assignedToString = assignedToUserInfo ? assignedToUserInfo.contactName : '';

          if ((claim.notification && claim.notification.toString() === 'true')) {
            claim.notification = 'NEW';
          } else {
            claim.notification = ' ';
          }
          // else {
          //   claim.notification = ' ';
          //   if (claim.comments && claim.comments.length > 0) {
          //     let lastCommentDate = DateTime.fromMillis(0);
          //     claim.comments.forEach((comment) => {
          //       if (comment.createDate > lastCommentDate) {
          //         if (comment.user.id !== currentUser.id) {
          //           claim.notification = 'NEW';
          //         } else {
          //           claim.notification = ' ';
          //         }
          //       }
          //     });
          //   }
          // }
          /* tslint:disable:no-string-literal */
          /* tslint:disable-next-line:max-line-length */
          /* tslint:disable:no-string-literal */
        });

        if (this.state.user!!.type === UserType.LCBOReporter) {
          claims = await this.transformClaims(claims);
        }
      }

      this.setState({ claims: claims });
      if (this.tableState) {
        this.handleTableFiltersChanged(this.tableState.filtered);
      }
    } catch (error) {
      console.log(error);
    }

    this.setState({ loading: false });
  }

  componentDidMount() {
    this.fetchData();
  }

  addLeadingZero(time: Number) {
    let str = time.toString();
    if (str.length < 2) {
      return '0' + str;
    }
    return str;
  }

  createFileName() {
    let today = new Date();
    let year = today.getFullYear();
    let day = this.addLeadingZero(today.getDate());
    let month = this.addLeadingZero(today.getMonth() + 1);
    let hour = this.addLeadingZero(today.getHours());
    let minute = this.addLeadingZero(today.getMinutes());
    let seconds = this.addLeadingZero(today.getSeconds());

    let dateName = `${year}-${month}-${day}__${hour}.${minute}.${seconds}EST`;
    var channelFilterName: string = '';

    if (this.state.user!!.type === UserType.LCBOReporter) {
      if (!this.state.channelFilter.length) return dateName + '_allChannels';
      var ifFirst: boolean = true;
      for (const channel of this.state.channelFilter) {
        channelFilterName += (ifFirst ? '' : '-') + Claims.channelMap[channel.id];
        ifFirst = false;
      }
    }
    return dateName + '_' + channelFilterName;
  }

  format = (data: any[], ifReporter: boolean) => {
    if (ifReporter) {
      return data.map(item => {
        return ([
          { text: item.id },
          { text: item.createDateString },
          { text: item.updateDateString },
          { text: item.goodsReceivedDateString },
          { text: item.purchaseNumber },
          { text: item.type },
          { text: item.statusText },
          { text: item.storeOperatorName },
          { text: item.storeNumber },
          { text: item.channelName },
          { text: item.item },
          { text: item.itemDescription },
          { text: item.cases },
          { text: item.units },
          { text: item.decision },
        ]);
      });
    } else {
      return data.map(item => {
        return ([
          { text: item.id },
          { text: item.createDateString },
          { text: item.statusText },
          { text: item.type },
          { text: item.storeOperatorName },
          { text: item.updateDateString },
        ]);
      });
    }
  }

  /**
   * Exports the claims as a PDF.
   * @param exportMode The type of export to perform.
   * false otherwise.
   */
  async downloadAsPdf(exportMode: ExportMode = ExportMode.All) {
    var channelFilterString: string = '';
    var channelFilterName: string = '';
    let claimType: ClaimType | undefined = undefined;
    if (this.state.channelFilter.length) {
      for (const channel of this.state.channelFilter) {
        channelFilterName = channelFilterName + ((channelFilterName !== '') ? '-' : '') + Claims.channelMap[channel.id];
        channelFilterString = channelFilterString + ((channelFilterString !== '') ? ',' : '') + channel.id;
      }
    } else {
      channelFilterName = 'allChannels';
      channelFilterString = 'exportAll';
    }
    channelFilterName = 'allChannels';
    channelFilterString = 'exportAll';
    try {
      let claimsToExport: number[] | undefined = undefined;
      switch (exportMode) {
        case ExportMode.Visible:
          const visibleData = this.getVisibleData();
          claimsToExport = visibleData && visibleData.map(({ id }) => id);
          break;
        case ExportMode.Filtered:
          const filteredData = this.getFilteredData();
          if (filteredData && filteredData.length > 0 && filteredData.map(({type}) => type).every((val, i, arr) => val === arr[0])) {
            claimType = filteredData[0].type;
          }
          claimsToExport = filteredData && filteredData.map(({ id }) => id);
          break;
        default:
          claimsToExport = undefined;
          break;
      }
      await ClaimService.downloadPDF(this.state.user, channelFilterString, channelFilterName,
        this.state.hideCompletedClaims, this.state.hideReturnInvoice, claimsToExport, claimType);
      Toast.present({ message: 'Claims has been successfully exported' }, ToastState.SUCCESS);
    } catch (err) {
      Toast.present({ message: 'Claims has failed to be exported' }, ToastState.ERROR);
    }
  }

  /**
   * Gets the claim data that is currently visible to the user.
   * @return The list of claims currently visible to the user.
   */
  getVisibleData(): Claim<Item>[] | undefined {
    const filteredData = this.getFilteredData();
    return filteredData && filteredData
      .slice(this.tableState.page * this.tableState.pageSize,
        (this.tableState.page + 1) * this.tableState.pageSize);
  }

  /**
   * Gets the claim data that is passing the current filters.
   * The data will also be sorted in the same way that the user has specified.
   * @return The list of claims in the user's filters, sorted according to the selected sort specifications.
   */
  getFilteredData(): Claim<Item>[] | undefined {
    return Claims.getFilteredData(this.tableState);
  }

  /**
   * Exports the claims data as a comma separated values file.
   * @param exportMode The subset of claims which should be exported. Defaults to all claims.
   */
  async downloadAsCsv(exportMode: ExportMode = ExportMode.All) {
    let dateName = this.createFileName();
    const options = {
      fieldSeparator: ',',
      filename: `LCO_Claims_Export__${dateName}`,
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      showTitle: false,
      useBom: false,
      useKeysAsHeaders: true,
    };
    const csvExporter = new ExportToCsv(options);
    var ifReporter = this.state.user!!.type === UserType.LCBOReporter;
    let cleanedClaims: Claim<Item>[] = [];
    let claimsToExport: Set<Claim<Item>['id']> | undefined = undefined;
    let type: ClaimType | null = null;
    function createMappedIterable<T, U>(iterable: Iterable<T>, mapFunction: (source: T) => U): Iterable<U> {
      return {
        *[Symbol.iterator]() {
          for (const item of iterable) {
            yield mapFunction(item);
          }
        }
      };
    }
    switch (exportMode) {
      case ExportMode.Visible:
        const visibleData = this.getVisibleData();
        claimsToExport = visibleData && new Set(createMappedIterable(visibleData, ({ id }) => id));
        if (visibleData && visibleData.length) {
          type = visibleData[0].type;
          for (const data of visibleData) {
            type = data.type === type ? type : null;
          }
        }
        break;
      case ExportMode.Filtered:
        const filteredData = this.getFilteredData();
        claimsToExport = filteredData && new Set(createMappedIterable(filteredData, ({ id }) => id));
        if (filteredData && filteredData.length) {
          type = filteredData[0].type;
          for (const data of filteredData) {
            type = data.type === type ? type : null;
          }
        }
        break;
      default:
        claimsToExport = undefined;
        break;
    }
    if (!ifReporter) {
      const isQAAdmin = this.state.user!!.type === UserType.QAAdmin;
      cleanedClaims = await this.cleanDataForCsvNonReporter(isQAAdmin, claimsToExport, type);
    } else {
      cleanedClaims = this.cleanDataForCsv(ifReporter, claimsToExport, type);
    }

    if (cleanedClaims.length < 1) {
      Toast.present({ message: 'Cannot export empty claims' }, ToastState.ERROR);
    } else {
      try {
        csvExporter.generateCsv(cleanedClaims);
        Toast.present({ message: 'Claims has been successfully exported' }, ToastState.SUCCESS);
      } catch (err) {
        Toast.present({ message: 'Claims has failed to be exported' }, ToastState.ERROR);
      }
    }
  }

  async cleanDataForCsvNonReporter(isQAAdmin: boolean, filterIds?: ReadonlySet<Claim<Item>['id']>, type?: ClaimType | null) {
    var queryParams = {};
    if (this.state.hideCompletedClaims) {
      let paramKey = 'hideCompleted';
      queryParams[paramKey] = true;
    }
    var queryString = Object.keys(queryParams).map(key => key + '=' + queryParams[key]).join('&');

    let allClaimsItems;
    if (isQAAdmin) {
      let alertClaims = await getAlertClaims([AlertType.QAAlert]);
      allClaimsItems = alertClaims.map((alertClaim) => alertClaim.toGenericClaim());
    } else {
      allClaimsItems = (await ClaimService.fetchAllItemsAcrossAllUser(queryString));
    }
    if (filterIds !== undefined) {
      allClaimsItems = allClaimsItems.filter((claim) => filterIds.has(claim.id));
    }

    const arrayOfStores = await StoreService.fetchAll();
    const arrayOfUsers = await UserService.getAllGrocerUsers();
    const arrayOfChannels = await ChannelService.fetchAll();

    let objOfStores = {};
    let objOfUsers = {};
    let objOfChannels = {};

    for (let store of arrayOfStores) {
      objOfStores[store.id] = {
        storeOperatorName: store.storeOperatorName,
        storeOperatorNumber: store.storeOperatorNumber,
        phoneNumber: store.phoneNumber,
        address: store.address,
        channelId: store.channelId
      };
    }
    for (let user of arrayOfUsers) {
      objOfUsers[user.id] = {
        contactName: user.contactName,
        email: user.email,
      };
    }

    for (let channel of arrayOfChannels) {
      objOfChannels[channel.id] = {
        channelName: channel.channelName
      };
    }

    allClaimsItems.forEach(async claim => {
      claim.storeOperatorName = objOfStores[claim.storeId].storeOperatorName;
      claim.storeNumber = objOfStores[claim.storeId].storeOperatorNumber;
      claim.phoneNumber = objOfStores[claim.storeId].phoneNumber;
      claim.address = objOfStores[claim.storeId].address;
      claim.channelId = objOfStores[claim.storeId].channelId;
      claim.createDateString = formatDate(claim.createDate);
      claim.updateDateString = claim.updateDate.toString() !== '-' ? formatDate(claim.updateDate) : '-';
      claim.contactName = objOfUsers[claim.userId] ? objOfUsers[claim.userId].contactName : '';
      claim.email = objOfUsers[claim.userId] ? objOfUsers[claim.userId].email : '';
      if (claim.channelId && objOfChannels[claim.channelId]) {
        claim.channelName = objOfChannels[claim.channelId].channelName;
      } else {
        claim.channelName = '-';
      }
    });

    allClaimsItems = await this.transformClaims(allClaimsItems);

    /* tslint:disable:no-string-literal */
    let newClaims: Claim<Item>[] = [];
    for (const claim of allClaimsItems) {
      if (!type || type === claim.type) {
        var newClaim = this.copyClaim(claim);
        newClaims.push(newClaim);
      }
    }
    let prevId = -2;
    let index = 0;
    newClaims.map(async obj => {
      // items part of the same claim should have PO numbers separated
      //  same id as previous obj indicates part of the same claim
      if (obj['id'] === prevId) index += 1;
      else index = 0;
      prevId = obj['id'];

      // Clean up date objects, rename headers, and order headers
      obj['ClaimId'] = obj['id'];
      obj['DateSubmitted'] = obj['createDateString'];
      obj['Status'] = obj['statusText'];
      obj['Type'] = obj['type'];
      if (
        this.state.user !== null
      ) {
        obj['Disposition'] =
          obj['disposition'] && obj['disposition'] !== null
            ? obj['disposition']
            : '';
        obj['Alert#'] = obj['alertId'] && obj['alertId'] !== null
            ? obj['alertId']
            : '';
      }
      obj['StoreName'] = obj['storeOperatorName'];
      if (isQAAdmin) obj['StoreManager'] = obj['storeOwnerName'] || '';
      obj['Channel'] = obj['channelName'];
      obj['Address'] = obj['address'];
      obj['PhoneNumber'] = obj['phoneNumber'];
      obj['ContactName'] = obj['contactName'];
      obj['Email'] = obj['email'];
      let purchaseNumber = obj['purchaseNumber'] || '';
      if (!obj['purchaseNumber']) {
        const purchaseNumberList = obj['purchaseNumberList'].split(',').map(poNumber => poNumber.trim());
        if (index < purchaseNumberList.length) purchaseNumber = purchaseNumberList[index];
      }
      obj['PO#'] = purchaseNumber;
      if (this.state.user !== null && this.state.user.type === UserType.Admin && !this.state.hideReturnInvoice) {
        obj['ReturnInvoice'] = obj['returnInvoice'] === null ? '' : obj['returnInvoice'];
      }
      obj['ReceivalDate'] = obj['goodsReceivedDateString'];
      obj['StoreNumber'] = obj['storeNumber'];
      obj['Item/SKU'] = obj['item'] !== -1 ? obj['item'] : 'Custom';
      obj['Category'] = obj['category'];
      obj['ProductName'] = obj['itemDescription'];
      obj['Cases'] = obj['cases'] ? obj['cases'] : '';
      obj['Units'] = obj['units'] ? obj['units'] : '';
      obj['Approved'] = obj['decision'] === 'Approved' ? 'Yes' : 'No';
      obj['StatusDateModified'] = obj['updateDateString'];

      // Delete unneccessary columns
      Object.keys(obj).forEach((key) => {
        if (key.charAt(0) === key.charAt(0).toLowerCase()) {
          delete obj[key];
        }
      });
    });
    return newClaims;
  }

  cleanDataForCsv(ifReporter: boolean, filterClaimIds?: ReadonlySet<Claim<Item>['id']>, type?: ClaimType | null) {
    /* tslint:disable:no-string-literal */
    let newClaims: Claim<Item>[] = [];
    for (const claim of this.state.claims) {
      if ((filterClaimIds === undefined || filterClaimIds.has(claim.id)) && (!type || type === claim.type)) {
        var newClaim = this.copyClaim(claim);
        newClaims.push(newClaim);
      }
    }
    let prevId = -2;
    let index = 0;
    if (ifReporter) {
      newClaims.map(async obj => {
        // items part of the same claim should have PO numbers separated
        //  same id as previous obj indicates part of the same claim
        if (obj['id'] === prevId) index += 1;
        else index = 0;
        prevId = obj['id'];
        
        // Clean up date objects, rename headers, and order headers
        obj['ClaimId'] = obj['id'];
        obj['DateSubmitted'] = obj['createDateString'];
        obj['StatusDateModified'] = obj['updateDateString'];
        obj['ReceivalDate'] = obj['goodsReceivedDateString'];
        let purchaseNumber = obj['purchaseNumber'] || '';
        if (!obj['purchaseNumber']) {
          const purchaseNumberList = obj['purchaseNumberList'].split(',').map(poNumber => poNumber.trim());
          if (index < purchaseNumberList.length) purchaseNumber = purchaseNumberList[index];
        }
        obj['PurchaseNumber'] = purchaseNumber;
        obj['Type'] = obj['type'];
        obj['Status'] = obj['statusText'];
        obj['StoreName'] = obj['storeOperatorName'];
        obj['StoreNumber'] = obj['storeNumber'];
        obj['Address'] = obj['address'];
        obj['PhoneNumber'] = obj['phoneNumber'];
        obj['ContactName'] = obj['contactName'];
        obj['Email'] = obj['email'];
        obj['Channel'] = obj['channelName'];
        obj['Item'] = obj['item'] !== -1 ? obj['item'] : 'Custom';
        obj['ItemDescription'] = obj['itemDescription'];
        obj['Cases'] = obj['cases'] ? obj['cases'] : '';
        obj['Units'] = obj['units'] ? obj['units'] : '';
        obj['Decision'] = obj['decision'];

        // Delete unneccessary columns
        Object.keys(obj).forEach((key) => {
          if (key.charAt(0) === key.charAt(0).toLowerCase()) {
            delete obj[key];
          }
        });
      });
    } else {
      newClaims.map(async obj => {
        // Clean up date objects, rename headers, and order headers
        obj['ClaimId'] = obj['id'];
        obj['DateSubmitted'] = obj['createDateString'];
        obj['Status'] = obj['statusText'];
        obj['Type'] = obj['type'];
        obj['StoreName'] = obj['storeOperatorName'];
        obj['Address'] = obj['address'];
        obj['PhoneNumber'] = obj['phoneNumber'];
        obj['ContactName'] = obj['contactName'];
        obj['Email'] = obj['email'];
        obj['StatusDateModified'] = obj['updateDateString'];

        // Delete unneccessary columns
        Object.keys(obj).forEach((key) => {
          if (key.charAt(0) === key.charAt(0).toLowerCase()) {
            delete obj[key];
          }
        });
      });
    }
    return newClaims;
    /* tslint:enable:no-string-literal */
  }

  toggleCompletedClaims() {
    this.setState({ hideCompletedClaims: !this.state.hideCompletedClaims, claims: [] },
      () => {
        this.fetchData();
      });
  }

  toggleReturnInvoice() {
    this.setState({ hideReturnInvoice: !this.state.hideReturnInvoice });
  }

  onSelectChange = (property: string, e) => {
    if (property === 'channel') {
      const element = (e === null) ? '' : e;
      let channels: Channel[] = [];
      for (const eachElement of element) {
        let channel: Channel;
        if (eachElement.id) {
          channel = { id: Number(eachElement.id) } as Channel;
        } else {
          channel = { id: Number(eachElement.value) } as Channel;
        }
        channels.push(channel);
      }
      this.setState({ channelFilter: channels }, () => { this.fetchData(); });
    }
  }

  copyClaim(claim: Claim<Item>) {
    return { ...claim };
  }

  onFetchData = (tableState: any, instance: any) => {
    this.setState({ loading: true });
    this.fetchData(tableState);
    this.setState({ loading: false });
  }

  render() {
    let { channelFilter, user } = this.state;
    let channelMultiSelectVal: number[] = [];
    if (channelFilter) {
      for (let channel of channelFilter) {
        channelMultiSelectVal.push(channel.id);
      }
    }
    let columns: any = [];
    if (user) {
      switch (user.type) {
        case UserType.LCBOReporter:
          columns = Claims.reporterColumns;
          break;
        case UserType.Admin:
          columns = Claims.columns.filter(col => ['disposition', 'alertId'].indexOf(col.accessor as string) === -1);
          break;
        case UserType.LCBO:
          columns = Claims.columns.filter(col => ['disposition', 'alertId'].indexOf(col.accessor as string) === -1);
          break;
        case UserType.QAAdmin:
          columns = Claims.columns.filter(col => ['type', 'storeOperatorName', 'itemPO', 'assignedToString'].indexOf(col.accessor as string) === -1);
          break;
        default:
          columns = Claims.columns;
          break;
      }
    }

    if (this.state.hideReturnInvoice) {
      columns = columns.filter(col => col.accessor as string !== 'returnInvoice');
    }
        
    const NoDataComponent = () => {
      return <NoDataMessage isLoading={this.state.loading} />;
    };

    const tooltipCellLinker = LinkedTooltipCell(linker, user && user.type === UserType.LCBOReporter);
    return (
      <div className="claims">
        <div className="claims-header">
          <div className="claims-header-left">
            <h1>Claims</h1>
            <div className="displayToggleButton">
              <input
                type="checkbox"
                onChange={() => this.toggleCompletedClaims()}
                defaultChecked={this.state.hideCompletedClaims}
              />
              <label>&nbsp;Hide Completed Claims</label>
            </div>
            {
              this.state.user !== null &&
              [UserType.Admin, UserType.LCBO, UserType.LCBOReporter].indexOf(this.state.user.type) !== -1 ? (
              <div className="displayToggleButton">
                <input
                  type="checkbox"
                  onChange={() => this.toggleReturnInvoice()}
                  defaultChecked={this.state.hideReturnInvoice}
                />
                <label>&nbsp;Hide Return Invoice</label>
              </div>
            ) : null
            }

            {user && user!!.type === UserType.LCBOReporter ? (
              <>
                <Select
                  className="dropdown"
                  multi={true}
                  value={channelMultiSelectVal}
                  disabled={!(!!this.state.claims)}
                  options={this.state.channelOptions}
                  placeholder="Select one or more channel(s)"
                  onChange={this.onSelectChange.bind(this, 'channel')}
                />
              </>
            ) : null}
          </div>
          <div className="claims-header-right">
            <DropdownButton className="exportButton" label="Export to CSV">
              <DropdownContentDiv>
                {!this.state.showExportFiltered ? (
                  <button
                    className="exportButton"
                    onClick={() => this.downloadAsCsv(ExportMode.All)}
                  >
                    {EXPORT_ALL_MESSAGE}
                  </button>
                ) : (
                  <button
                    className="exportButton"
                    onClick={() => this.downloadAsCsv(ExportMode.Filtered)}
                  >
                    {EXPORT_FILTERED_MESSAGE}
                  </button>
                )}
                <button
                  className="exportButton"
                  onClick={() => this.downloadAsCsv(ExportMode.Visible)}
                >
                  {EXPORT_VISIBLE_MESSAGE}
                </button>
              </DropdownContentDiv>
            </DropdownButton>
            <DropdownButton className="exportButton" label="Export to PDF">
              <DropdownContentDiv>
                {!this.state.showExportFiltered ? (
                  <button
                    className="exportButton"
                    onClick={() => this.downloadAsPdf(ExportMode.All)}
                  >
                    {EXPORT_ALL_MESSAGE}
                  </button>
                ) : (
                  <button
                    className="exportButton"
                    onClick={() => this.downloadAsPdf(ExportMode.Filtered)}
                  >
                    {EXPORT_FILTERED_MESSAGE}
                  </button>
                )}
                <button
                  className="exportButton"
                  onClick={() => this.downloadAsPdf(ExportMode.Visible)}
                >
                  {EXPORT_VISIBLE_MESSAGE}
                </button>
              </DropdownContentDiv>
            </DropdownButton>
          </div>
        </div>
        <ReactTable
          defaultPageSize={50}
          minRows={0}
          filterable={true}
          defaultSorted={[{ id: 'createDate', asc: true }]}
          columns={columns}
          // manual={true}
          data={this.state.claims}
          // pages={this.state.pages}
          loading={this.state.loading}
          // onFetchData={this.onFetchData}
          LoadingComponent={TableLoader}
          NoDataComponent={NoDataComponent}
          column={{
            ...ReactTableDefaults.column,
            className: 'table-item',
            Cell: tooltipCellLinker,
          }}
          onFilteredChange={this.handleTableFiltersChanged}
        >
          {this.handleTableRender}
        </ReactTable>
      </div>
    );
  }

  private handleTableFiltersChanged(filters: { id: string, value: null | {} }[]): void {
    this.setState({
      showExportFiltered: filters.some((filter) => filter.value !== null),
    });
  }

  private handleTableRender(state: any, makeDefaultTable: () => JSX.Element) {
    this.tableState = state;
    return makeDefaultTable();
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/views/Claims/index.tsx